import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { readTokenSync, writeToken, clearToken, hydrateTokenFromNative } from "@/lib/tokenStore";
import { initIAP } from "@/lib/iap";
import { identifyUser, track, resetIdentity } from "@/lib/analytics";

const AuthContext = createContext(null);

// Module-level guard so concurrent logout clicks (e.g. mobile users
// double-tapping) can't trigger half-clean states.
let _logoutInFlight = false;

// Wipe every client-side trace of the user's session in a defensive order:
// localStorage → sessionStorage → cookies readable by JS → service worker
// caches. Synchronous. Safe to call repeatedly.
function hardClearClientSession() {
  // 1. Bearer token + any namespaced preferences keyed to the user.
  clearToken().catch(() => { /* non-fatal — best effort */ });
  // 2. SessionStorage (analytics breadcrumbs, in-flight onboarding answers).
  try { sessionStorage.clear(); } catch { /* ignore */ }
  // 3. Any cookies we can see from JS (HttpOnly cookies remain — only the
  //    backend can clear those, which it now does with matching attributes).
  try {
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (!name) return;
      // Clear on current path + root, with and without domain, to be safe.
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
    });
  } catch { /* ignore */ }
  // 4. Unregister any push subscriptions — they're tied to the logged-out
  //    user and we don't want them firing notifications for the next person
  //    to sign in on this device. Fire-and-forget; failure is non-fatal.
  try {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration("/").then((reg) => {
        reg?.pushManager?.getSubscription?.().then((sub) => sub?.unsubscribe?.());
      }).catch(() => {});
    }
  } catch { /* ignore */ }
}

export function AuthProvider({ children }) {
  // user states:  null = checking, false = anonymous, object = authed
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    // Hydrate the token from native storage FIRST — on iOS Capacitor
    // WKWebView localStorage may have been evicted while the token still
    // lives in NSUserDefaults via @capacitor/preferences.
    const token = await hydrateTokenFromNative();
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Re-identify on session resume so PostHog links the anonymous
      // pre-load pageview to this user. Only user_id + non-PII metadata.
      try {
        if (data?.id) identifyUser(data.id, {
          role: data.role,
          premium: data.premium,
          signup_date: data.created_at,
        });
      } catch { /* never break auth on analytics failure */ }
      // Initialise native Apple StoreKit with our backend user.id so the
      // receipt can be correlated with the correct account. No-op on web.
      if (data?.id) initIAP(data.id);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Server explicitly rejected the token — clear from both stores.
        await clearToken();
        setUser(false);
      } else {
        // Transient error (network hiccup, backend cold-start). Keep the
        // token so the next mount / reload can retry authoritatively.
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) writeToken(data.token);
    setUser(data);
    try {
      if (data?.id) identifyUser(data.id, {
        role: data.role,
        premium: data.premium,
        signup_date: data.created_at,
      });
      track("login_completed");
    } catch { /* ignore */ }
    if (data?.id) initIAP(data.id);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data.token) writeToken(data.token);
    setUser(data);
    try {
      if (data?.id) identifyUser(data.id, {
        role: data.role,
        premium: data.premium,
        signup_date: data.created_at,
      });
      track("signup_completed");
    } catch { /* ignore */ }
    if (data?.id) initIAP(data.id);
    return data;
  };

  const logout = async () => {
    if (_logoutInFlight) return;
    _logoutInFlight = true;
    // 1. Wipe local session SYNCHRONOUSLY first, so even if the backend
    //    call hangs (e.g. iOS PWA loses connection mid-tap) the user is
    //    locally signed out the moment they tap the button.
    hardClearClientSession();
    try { track("logout"); resetIdentity(); } catch { /* ignore */ }
    setUser(false);
    // 2. Then tell the backend to clear its cookie (matching SameSite/Secure
    //    attributes — see set_auth_cookie / delete_cookie in server.py).
    try {
      await api.post("/auth/logout");
    } catch {
      /* network error is non-fatal — the local state is already clean. */
    }
    // 3. Hard-reload to the Welcome route. Using location.replace (not
    //    React Router navigate) guarantees:
    //      - the React tree is fully torn down (no cached @tanstack/query
    //        results carrying the old user's identity),
    //      - any in-flight requests are aborted,
    //      - the next AuthProvider mount starts from a clean slate.
    //    `replace` so the user can't tap the back button to get back into
    //    the previous session.
    try { window.location.replace("/"); } catch { /* ignore */ }
  };

  const refresh = fetchMe;

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
